import {
  AgGridClient,
  CustomNotification,
  StyledModal,
  dialogFunction,
} from 'common/components';
import { Typography, Image } from 'antd';
import { useMemo } from 'react';
import WalmartLogo from 'assets/retailer/walmart.png';
import { green } from '@ant-design/colors';
import {
  useGetWalmartProductData,
  usePostToWalmart,
  useSaveWalmartFormValueWhenExit,
} from './queries';
import { cleanInvalidData } from './post-product-form/utils';
import { useDispatch } from 'react-redux';
import { useValidateWalmartFormData } from './hooks';

import { setPostToWalmartFormValues } from 'pages/branded-products/components/post-to-walmart-krogger/slice';
import { isArray } from 'lodash';
import { useParams } from 'react-router-dom';

const { Paragraph, Text } = Typography;

const ModalConfirmPostToWalmartKrogger = ({
  visible,
  onCancel,
  validationList,
  walmartFormValues,
  needToRepublishList,
  onSuccess,
}) => {
  const { id: productId } = useParams();

  const mutationPostToWalmart = usePostToWalmart();

  const { saveWalmartFormMutation } = useSaveWalmartFormValueWhenExit();

  const { handleValidateWalmartProductData } = useValidateWalmartFormData();

  const { initialWalmartProductDataList } = useGetWalmartProductData({
    enabled: false,
    productIds: [+productId],
  });

  const dispatch = useDispatch();

  const onPostToWalmartFail = async () => {
    try {
      const formattedFormValues = cleanInvalidData(walmartFormValues);
      const productMatchingData = formattedFormValues?.map((val) => ({
        ...val,
        needToRepublish:
          isArray(needToRepublishList) &&
          needToRepublishList?.find(
            (item) => val?.productId === item?.productId
          )?.needToRepublish,
      }));
      const response = await saveWalmartFormMutation.mutateAsync({
        productMatchingData,
      });
      const { isSuccess, message } = response || {};
      if (isSuccess) {
        initialWalmartProductDataList();
      } else {
        CustomNotification.error(message || 'Can not save Walmart form!');
      }
      dispatch(setPostToWalmartFormValues(formattedFormValues));
      handleValidateWalmartProductData(formattedFormValues);
    } catch (error) {
      CustomNotification.error('Can not save Walmart form!');
    }
  };

  const handlePostToWalmart = () => {
    dialogFunction({
      type: 'warn',
      content:
        'Are you sure you want to post product(s) in the table to Walmart?',
      onOk: async () => {
        const formattedFormValues = cleanInvalidData(walmartFormValues);
        const productMatchingData = formattedFormValues?.map((val) => ({
          ...val,
          needToRepublish:
            isArray(needToRepublishList) &&
            needToRepublishList?.find(
              (item) => val?.productId === item?.productId
            )?.needToRepublish,
        }));
        await mutationPostToWalmart
          .mutateAsync({ productMatchingData })
          .then(() => {
            CustomNotification.success('Post to Walmart successfully');
            onSuccess();
          })
          .catch((e) => {
            CustomNotification.error(e);
            onPostToWalmartFail();
          });
      },
    });
  };

  const gridData = useMemo(() => {
    return validationList?.map((validationItem, idx) => {
      const { dataErrors, ...restProperties } = validationItem || {};

      return { ...restProperties, id: idx };
    });
  }, [JSON.stringify(validationList)]);

  return (
    <StyledModal
      visible={visible}
      title='Post to Walmart List'
      width={1100}
      bodyStyle={{
        minHeight: 'min(60vh, 100px)',
        maxHeight: 'min(65vh, 800px)',
      }}
      onCancel={onCancel}
      okText='Submit'
      onOk={handlePostToWalmart}
    >
      <div
        style={{
          border: '1px solid #c2cfe0',
          padding: 6,
          display: 'inline-block',
          borderRadius: 4,
          marginBottom: 16,
        }}
      >
        <Paragraph style={{ marginBottom: 0 }}>
          <Text>
            <RoundedNumber number={validationList?.length} /> product
            {validationList?.length > 1 ? '(s) are' : ' is'} valid and ready to
            post to
          </Text>
          <Image
            src={WalmartLogo}
            width={100}
            preview={false}
            style={{ marginTop: -2 }}
          />
        </Paragraph>
      </div>

      <Paragraph>
        The following products will be sent to Walmart for their review.{' '}
        <Text type='danger'>
          RIVIR must be registered as the content provider with Walmart – Please
          refer to the Publication section under{' '}
          <a href='company?tab=system' target='_blank'>
            My Company Profile {'>'} System Settings
          </a>{' '}
          for more details.
        </Text>
      </Paragraph>
      <Paragraph>
        Please note that Walmart expects suppliers register their products
        (GTIN’s) that contain chemicals, pesticides, aerosols, batteries
        (lithium and lead-acid), or data with keywords like "antibacterial" or
        "disinfectant” with WERCSmart, a 3rd party company.
      </Paragraph>
      <div style={{ height: 300 }}>
        <AgGridClient
          columnDefs={columnDefs}
          rowData={gridData}
          suppressRowClickSelection
        />
      </div>
    </StyledModal>
  );
};

export const RoundedNumber = ({ number }) => {
  return (
    <Text
      style={{
        background: green[5],
        color: 'white',
        borderRadius: 100,
        display: 'inline-block',
        minWidth: 20,
        textAlign: 'center',
      }}
    >
      {number}
    </Text>
  );
};

const columnDefs = [
  {
    headerName: 'GTIN14',
    field: 'gtin',
    flex: 1,
  },
  {
    headerName: 'UPC12',
    field: 'upc12',
    flex: 1,
  },
  {
    headerName: 'Brand',
    field: 'brand',
    flex: 1,
  },
  {
    headerName: 'Product Name',
    field: 'productName',
    flex: 1,
  },
  {
    headerName: 'Size',
    field: 'size',
    flex: 1,
  },
  {
    headerName: 'UOM',
    field: 'uom',
    flex: 1,
  },
];

export default ModalConfirmPostToWalmartKrogger;
